import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageContainer } from '@/components/layout'
import { Card, Button, Toggle, Input, Tabs, Spinner, ToastContainer, useToast } from '@/components/ui'
import { useAuth } from '@/hooks/useAuth'
import { useSubscription } from '@/context/SubscriptionContext'
import { PlanUpgradeWizard } from '@/components/PlanUpgradeWizard'
import { api, ApiError } from '@/services/api'
import { getErrorMessage } from '@/services/errors'
import type { UserSettings, SubscriptionStatus } from '@/services/types'

const settingsTabs = [
  { id: 'general', label: 'General' },
  { id: 'plan', label: 'Plan' },
  { id: 'notifications', label: 'Notifications' },
]

export function SettingsPage() {
  const { user } = useAuth()
  const { usage, refreshUsage } = useSubscription()
  const { toasts, addToast, dismissToast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const validTabs = settingsTabs.map((t) => t.id)
  const initialTab = validTabs.includes(searchParams.get('tab') || '') ? searchParams.get('tab')! : 'general'
  const [activeTab, setActiveTab] = useState(initialTab)
  const [settings, setSettings] = useState<UserSettings | null>(null)
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [showUpgradeWizard, setShowUpgradeWizard] = useState(false)

  const loadData = useCallback(async () => {
    try {
      const [settingsRes, subRes] = await Promise.all([
        api.getSettings(),
        api.getSubscriptionStatus().catch(() => null),
      ])
      setSettings(settingsRes.settings)
      if (subRes) setSubscription(subRes.subscription)
    } catch (err) {
      addToast(err instanceof ApiError ? getErrorMessage(err, 'Failed to load settings') : 'Failed to load settings', 'error')
    } finally {
      setIsLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleSaveSettings = async () => {
    if (!settings) return
    setIsSaving(true)
    try {
      const response = await api.updateSettings(settings)
      setSettings(response.settings)
      addToast('Settings saved', 'success')
    } catch (err) {
      addToast(err instanceof ApiError ? getErrorMessage(err, 'Failed to save settings') : 'Failed to save settings', 'error')
    } finally {
      setIsSaving(false)
    }
  }

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    setSearchParams(tab === 'general' ? {} : { tab }, { replace: true })
    if (tab === 'plan') {
      refreshUsage()
    }
  }

  const updateSetting = <K extends keyof UserSettings>(key: K, value: UserSettings[K]) => {
    setSettings((prev) => (prev ? { ...prev, [key]: value } : null))
  }



  if (isLoading) {
    return (
      <PageContainer title="Settings">
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer title="Settings">
      <Tabs tabs={settingsTabs} activeTab={activeTab} onTabChange={handleTabChange} />

      <div className="mt-6">
        {activeTab === 'general' && settings && (
          <div className="space-y-6">
            <Card>
              <h3 className="font-medium mb-4">Account</h3>
              <div className="space-y-4">
                <Input label="Email" value={user?.email || ''} disabled />
                <p className="text-xs text-secondary-text">
                  Contact support to change your email address.
                </p>
              </div>
            </Card>

            <Card>
              <h3 className="font-medium mb-4">Content Generation</h3>
              <div className="space-y-4">
                <div>
                  <label className="label">Max Post Length</label>
                  <Input
                    type="number"
                    value={settings.content_max_length ?? 280}
                    onChange={(e) =>
                      updateSetting('content_max_length', parseInt(e.target.value) || 280)
                    }
                    min={1}
                    max={280}
                  />
                </div>

                <Toggle
                  label="Enable auto-posting"
                  checked={settings.auto_post_enabled}
                  onChange={(e) => updateSetting('auto_post_enabled', e.target.checked)}
                />
              </div>
            </Card>

            <div className="flex justify-end">
              <Button onClick={handleSaveSettings} loading={isSaving}>
                Save Settings
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'plan' && (
          <div className="space-y-6">
            {showUpgradeWizard && usage ? (
              <PlanUpgradeWizard
                currentPlan={usage.plan}
                onCancel={() => setShowUpgradeWizard(false)}
              />
            ) : (
              <>
                <Card>
                  <h3 className="font-medium mb-4">Current Plan</h3>
                  {usage ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-2xl font-medium">
                            {usage.plan.charAt(0).toUpperCase() + usage.plan.slice(1)}
                          </p>
                          <p className="text-sm text-secondary-text mt-1">
                            {usage.plan === 'free'
                              ? 'Free tier — 15 lifetime generations'
                              : usage.plan === 'solo'
                                ? '$9/mo — 50 generations per month'
                                : usage.plan === 'pro'
                                  ? '$29/mo — 200 generations per month'
                                  : '$79/mo — Unlimited generations'}
                          </p>
                        </div>
                        <Button onClick={() => setShowUpgradeWizard(true)} variant={usage.plan === 'free' ? 'primary' : 'secondary'}>
                          {usage.plan === 'free' ? 'Upgrade' : 'Change plan'}
                        </Button>
                      </div>

                      {subscription && subscription.plan !== 'free' && subscription.current_period_end && (
                        <div className="border-t border-border pt-4">
                          <p className="text-sm text-secondary-text">
                            {subscription.cancel_at_period_end
                              ? `Cancels on ${new Date(subscription.current_period_end).toLocaleDateString()}`
                              : `Renews on ${new Date(subscription.current_period_end).toLocaleDateString()}`}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Spinner size="sm" />
                      <span className="text-secondary-text">Loading plan info...</span>
                    </div>
                  )}
                </Card>

                <Card>
                  <h3 className="font-medium mb-4">Usage</h3>
                  {usage ? (() => {
                    const used = usage.generations_used ?? 0
                    const limit = usage.generations_limit ?? 0
                    const remaining = typeof usage.generations_remaining === 'number'
                      ? usage.generations_remaining
                      : limit - used

                    return (
                      <div className="space-y-4">
                        {usage.is_unlimited ? (
                          <p className="text-sm text-secondary-text">
                            Unlimited generations — no cap on your plan.
                          </p>
                        ) : (
                          <>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-secondary-text">Generations used</span>
                              <span className="font-medium">
                                {used} / {limit}
                              </span>
                            </div>
                            <div className="w-full h-2 bg-secondary-bg rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  remaining <= 5 ? 'bg-red-500' : remaining < 10 ? 'bg-amber-500' : 'bg-primary-text'
                                }`}
                                style={{
                                  width: `${limit > 0 ? Math.min(100, (used / limit) * 100) : 0}%`,
                                }}
                              />
                            </div>
                            <p className={`text-sm ${
                              remaining <= 5 ? 'text-red-600' : remaining < 10 ? 'text-amber-600' : 'text-secondary-text'
                            }`}>
                              {remaining} generation{remaining !== 1 ? 's' : ''} remaining
                              {usage.plan === 'free' ? ' (lifetime)' : ' this period'}
                            </p>
                          </>
                        )}
                      </div>
                    )
                  })() : (
                    <div className="flex items-center gap-2">
                      <Spinner size="sm" />
                      <span className="text-secondary-text">Loading usage...</span>
                    </div>
                  )}
                </Card>
              </>
            )}
          </div>
        )}

        {activeTab === 'notifications' && settings && (
          <div className="space-y-6">
            <Card>
              <h3 className="font-medium mb-4">Email Notifications</h3>
              <div className="space-y-4">
                <Toggle
                  label="Email when content is posted"
                  checked={settings.email_on_post}
                  onChange={(e) => updateSetting('email_on_post', e.target.checked)}
                />
                <Toggle
                  label="Email on engagement milestones"
                  checked={settings.email_on_engagement}
                  onChange={(e) => updateSetting('email_on_engagement', e.target.checked)}
                />
                <Toggle
                  label="Weekly email digest"
                  checked={settings.email_digest}
                  onChange={(e) => updateSetting('email_digest', e.target.checked)}
                />
              </div>
            </Card>

            <div className="flex justify-end">
              <Button onClick={handleSaveSettings} loading={isSaving}>
                Save Settings
              </Button>
            </div>
          </div>
        )}
      </div>

      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </PageContainer>
  )
}
